import { LG_MEDIA_QUERY } from '@/hooks/useMediaQuery';
import { lazyOverlay } from '@/routing/lazyRoute';
import { isDrawerPanelView } from '@/shell/drawerPanel';
import { shellDrawsNavigation } from '@/shell/shellDetection';
import type { ViewNavigationListProps } from './ViewNavigationList';
import type { AllWorkAreasViewsListProps } from './AllWorkAreasViewsList';

/**
 * T-0591: the sidebar's view tree, its own chunk. Drawing a view's icon and dropping a view the
 * engine cannot read run the view catalog, and the catalog is the whole view engine, about 100 KB
 * minified. The tree is the last thing in the sidebar and fills in from the views the server sends,
 * so it waits for its chunk without moving anything else.
 *
 * `preloadSidebarViews` asks for the chunk at boot, beside the session check, so it is in by the
 * time the work area is known and the group is drawn in the same commit as the page beside it
 * (batch 485: through Suspense it came 297–300 ms after, see `lazyOverlay`).
 */
export const ViewNavigationList = lazyOverlay<
  typeof import('./ViewNavigationList'),
  ViewNavigationListProps
>(
  () => import('./ViewNavigationList'),
  (module) => module.ViewNavigationList,
);

export const AllWorkAreasViewsList = lazyOverlay<
  typeof import('./AllWorkAreasViewsList'),
  AllWorkAreasViewsListProps
>(
  () => import('./AllWorkAreasViewsList'),
  (module) => module.AllWorkAreasViewsList,
);

/**
 * Asks for the sidebar's view tree as the shell mounts, beside the work area's own reads, where a
 * sidebar is drawn at once: pinned beside the page (`lg` and wider), or the More drawer's own panel.
 *
 * T-0559: a phone or a narrow window draws its sidebar only when its menu opens, so there the view
 * tree is asked for once the launch is done and the browser is idle. The iPhone app's tabs never draw
 * one (its menu is the drawer's own web view), so they never ask. At boot it was 12 of a Decisions
 * launch's 44 chunks, the view engine among them, fetched and run beside the launch screen's own for
 * a sidebar nobody saw.
 */
export function preloadSidebarViews(): void {
  if (sidebarDrawnAtOnce()) {
    loadSidebarViews();
  } else if (!shellDrawsNavigation() && !idleLoadScheduled) {
    idleLoadScheduled = true;
    // The preload lives for the page; nothing unmounts it, so its cancel is not kept.
    void whenIdle(loadSidebarViews);
  }
}

let idleLoadScheduled = false;

function sidebarDrawnAtOnce(): boolean {
  return window.matchMedia(LG_MEDIA_QUERY).matches || isDrawerPanelView();
}

function loadSidebarViews(): void {
  void ViewNavigationList.preload();
  void AllWorkAreasViewsList.preload();
}

/**
 * Once the launch is done and the browser is idle, as the command palette's code is (`Layout`).
 * Returns the way to cancel it (I-0360: no timer is armed with its handle thrown away).
 */
function whenIdle(load: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(load, { timeout: 3_000 });
    return () => window.cancelIdleCallback(handle);
  }
  const handle = setTimeout(load, 1_500);
  return () => clearTimeout(handle);
}
